import React, { useState, useEffect } from 'react';
import { submitProgramRegistration, getProgramAvailability } from '../lib/dataService';
import { toast } from 'sonner';
import { Send, User, Users, ShieldCheck, CalendarRange, Shirt, UserCheck } from 'lucide-react';
import './registration.css';

export const ACADEMY_TERMS = [
  'Term 1 (September - December)',
  'Term 2 (January - March)',
  'Term 3 (April - June)',
];
export const ACADEMY_AGE_GROUPS = [
  'Age 7-9: 9:00 - 10:00 am',
  'Age 10-12: 10:00 - 11:15 am',
  'Age 13-16: 11:15 am - 12:30 pm',
];
export const JERSEY_SIZES = [
  'Youth Small', 'Youth Medium', 'Youth Large', 'Youth XL',
  'Adult Small', 'Adult Medium', 'Adult Large', 'Adult XL', 'Adult XXL',
];

export default function ProgramRegistrationForm({ program, programLabel }) {
  const [form, setForm] = useState({
    child_name: '', child_dob: '', child_school: '', medical_conditions: '',
    parent1_name: '', parent1_email: '', parent1_phone: '',
    parent2_name: '', parent2_email: '', parent2_phone: '',
    age_group: '', jersey_size: '',
  });
  const [terms, setTerms] = useState([]);
  const [consent, setConsent] = useState(false);
  const [sending, setSending] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [availability, setAvailability] = useState(null);
  const isAcademy = program === 'academy';

  useEffect(() => {
    getProgramAvailability(program).then(setAvailability).catch(() => setAvailability(null));
  }, [program]);

  const toggleTerm = (t) => {
    setTerms((prev) => prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]);
  };

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.child_name.trim()) { toast.error("Please enter the child's full name."); return; }
    if (!form.child_dob.trim()) { toast.error("Please enter the child's date of birth."); return; }
    if (!form.child_school.trim()) { toast.error("Please enter the child's school."); return; }
    if (!form.parent1_name.trim() || !form.parent1_email.trim() || !form.parent1_phone.trim()) {
      toast.error("Parent 1's name, email and phone number are required."); return;
    }
    if (isAcademy) {
      if (terms.length === 0) { toast.error('Please select at least one term.'); return; }
      if (!form.age_group) { toast.error('Please select an age group program.'); return; }
      if (!form.jersey_size) { toast.error("Please select your child's jersey size."); return; }
    }
    if (!consent) { toast.error('Please accept the disclaimer and photo consent.'); return; }
    setSending(true);
    try {
      await submitProgramRegistration({ ...form, program, terms, consent });
      setSubmitted(true);
      getProgramAvailability(program).then(setAvailability).catch(() => {});
      toast.success('Registration submitted successfully!');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to submit registration. Please try again.');
    }
    setSending(false);
  };

  if (submitted) {
    return (
      <div className="reg-form-wrap">
        <div className="reg-success">
          <ShieldCheck size={44} />
          <h3>Registration Received!</h3>
          <p><strong>{form.child_name}</strong> is now registered for the <strong>{programLabel}</strong>.</p>
          <p>A confirmation email with your program details has been sent to <strong>{form.parent1_email}</strong>. We look forward to seeing you!</p>
        </div>
      </div>
    );
  }

  if (availability && availability.full) {
    return (
      <div className="reg-form-wrap" id="register">
        <div className="reg-header">
          <h2>{programLabel} Registration</h2>
        </div>
        <div className="reg-full-notice">
          <UserCheck size={38} />
          <h3>Program Full</h3>
          <p>All <strong>{availability.capacity} spaces</strong> for the {programLabel} are currently taken.</p>
          <p>Please contact <a href="mailto:info@caymanbasketball.com">info@caymanbasketball.com</a> to be added to the waiting list.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="reg-form-wrap" id="register">
      <div className="reg-header">
        <h2>{programLabel} Registration</h2>
        {availability && availability.capacity != null && (
          <div className={`reg-availability ${availability.available <= 5 ? 'low' : ''}`}>
            <UserCheck size={16} />
            <span><strong>{availability.available}</strong> of {availability.capacity} spaces available</span>
          </div>
        )}
        <p>Register your child below. The highlighted fields show where information needs to be entered — fields marked <span className="reg-req">*</span> are required.</p>
      </div>

      <form onSubmit={handleSubmit}>
        <section className="reg-card">
          <h3><User size={19} /> Child's Information</h3>
          <div className="reg-grid reg-grid-3">
            <label>Full Name <span className="reg-req">*</span>
              <input type="text" className="reg-input" value={form.child_name} onChange={set('child_name')} required />
            </label>
            <label>Date of Birth <span className="reg-req">*</span>
              <input type="date" className="reg-input" value={form.child_dob} onChange={set('child_dob')} required />
            </label>
            <label>School <span className="reg-req">*</span>
              <input type="text" className="reg-input" value={form.child_school} onChange={set('child_school')} required />
            </label>
          </div>
          <div className="reg-grid" style={{ marginTop: 16 }}>
            <label>Medical conditions we should be aware of
              <textarea
                className="reg-input"
                rows={3}
                value={form.medical_conditions}
                onChange={set('medical_conditions')}
                placeholder="E.g. asthma, allergies, epilepsy — write 'None' if not applicable"
              />
            </label>
            <p className="reg-confidential-note">
              This information will be kept strictly confidential and will help our coaches to be better
              equipped in their interactions with your child.
            </p>
          </div>
        </section>

        {isAcademy && (
          <section className="reg-card">
            <h3><CalendarRange size={19} /> Program Selection</h3>
            <label className="reg-block-label">Which term(s) are you registering for? <span className="reg-req">*</span></label>
            <div className="reg-terms">
              {ACADEMY_TERMS.map((t) => (
                <label key={t} className={`reg-term-option ${terms.includes(t) ? 'selected' : ''}`}>
                  <input type="checkbox" checked={terms.includes(t)} onChange={() => toggleTerm(t)} />
                  <span>{t}</span>
                </label>
              ))}
            </div>
            <div className="reg-grid reg-grid-2" style={{ marginTop: 18 }}>
              <label>Age Group Program <span className="reg-req">*</span>
                <select className="reg-input" value={form.age_group} onChange={set('age_group')} required>
                  <option value="">Select an age group</option>
                  {ACADEMY_AGE_GROUPS.map((g) => <option key={g} value={g}>{g}</option>)}
                </select>
              </label>
              <label><Shirt size={13} style={{ display: 'inline', marginRight: 4 }} />Child's Jersey Size <span className="reg-req">*</span>
                <select className="reg-input" value={form.jersey_size} onChange={set('jersey_size')} required>
                  <option value="">Select a size</option>
                  {JERSEY_SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </label>
            </div>
            <p className="reg-confidential-note" style={{ marginTop: 14 }}>
              An Academy Jersey is <strong>$25.00</strong> — this can be collected when you check in.
            </p>
          </section>
        )}

        <section className="reg-card">
          <h3><Users size={19} /> Parent / Guardian Information</h3>
          <div className="reg-official">
            <h4>Parent / Guardian 1</h4>
            <div className="reg-grid reg-grid-3">
              <label>Full Name <span className="reg-req">*</span>
                <input type="text" className="reg-input" value={form.parent1_name} onChange={set('parent1_name')} required />
              </label>
              <label>Email <span className="reg-req">*</span>
                <input type="email" className="reg-input" value={form.parent1_email} onChange={set('parent1_email')} required />
              </label>
              <label>Phone <span className="reg-req">*</span>
                <input type="tel" className="reg-input" value={form.parent1_phone} onChange={set('parent1_phone')} required />
              </label>
            </div>
          </div>
          <div className="reg-official">
            <h4>Parent / Guardian 2 <span className="reg-optional">(optional)</span></h4>
            <div className="reg-grid reg-grid-3">
              <label>Full Name
                <input type="text" className="reg-input" value={form.parent2_name} onChange={set('parent2_name')} />
              </label>
              <label>Email
                <input type="email" className="reg-input" value={form.parent2_email} onChange={set('parent2_email')} />
              </label>
              <label>Phone
                <input type="tel" className="reg-input" value={form.parent2_phone} onChange={set('parent2_phone')} />
              </label>
            </div>
          </div>
        </section>

        <section className="reg-card">
          <h3><ShieldCheck size={19} /> Disclaimer & Photo Consent</h3>
          <label className="reg-declaration">
            <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} />
            <span>
              I understand that participation in basketball activities involves a risk of injury, and I consent to my
              child's participation in the {programLabel}. I confirm that the information provided is accurate, and I
              authorize CIBA staff to seek emergency medical treatment for my child if needed. I also consent to
              photographs and videos taken during program sessions being used by CIBA for promotional purposes.
              I agree to receive program notices and payment reminders by email. <span className="reg-req">*</span>
            </span>
          </label>
        </section>

        <button type="submit" className="btn-primary reg-submit" disabled={sending}>
          <Send size={16} /> {sending ? 'Submitting...' : 'Register'}
        </button>
      </form>
    </div>
  );
}
